import { configuration } from "./Configuration";
import { authenticatedFetch } from "./JWTAuthService";

// Client for the tri-server document-cards endpoints. Mirrors KnowledgeBasesAPI: hand-written
// over authenticatedFetch, customer-scoped paths, no generated client.

const baseUrl = configuration.serverUrl;

export type DocumentCardStatus =
  | "pending"
  | "generating"
  | "generated"
  | "failed"
  | "stale";

export interface DocumentCardTocEntry {
  heading: string;
  level: number;
}

export interface DocumentCard {
  _id?: string;
  managedFileId: string;
  title: string;
  fileName?: string;
  synopsis: string;
  toc?: DocumentCardTocEntry[];
  topics: string[];
  entities?: string[];
  status: DocumentCardStatus;
  editedByUser: boolean;
  attempts?: number;
  generatedAt?: string;
  updatedAt?: string;
  error?: string;
}

export interface DocumentCardCoverage {
  total: number;
  generated: number;
  stale: number;
  pending: number;
  failed: number;
  editedByUser: number;
}

export interface DocumentCardsResponse {
  enabled: boolean;
  cards: DocumentCard[];
  coverage: DocumentCardCoverage;
}

const kbBase = (customerID: string, kbId: string) =>
  `${baseUrl}/${customerID}/knowledge-bases/${kbId}/document-cards`;

export const getDocumentCards = async (
  customerID: string,
  kbId: string
): Promise<DocumentCardsResponse> => {
  const response = await authenticatedFetch(kbBase(customerID, kbId), {
    method: "GET",
  });
  if (!response.ok) {
    throw new Error(`getDocumentCards failed: ${response.status}`);
  }
  return response.json();
};

export const setDocumentCardsEnabled = async (
  customerID: string,
  kbId: string,
  enabled: boolean
): Promise<{ enabled: boolean }> => {
  const response = await authenticatedFetch(`${kbBase(customerID, kbId)}/enabled`, {
    method: "PUT",
    body: JSON.stringify({ enabled }),
  });
  if (!response.ok) {
    throw new Error(`setDocumentCardsEnabled failed: ${response.status}`);
  }
  return response.json();
};

// Marks cards pending and triggers an immediate single-KB sweep server-side. Returns
// straight away; poll getDocumentCards for progress.
export const generateDocumentCards = async (
  customerID: string,
  kbId: string,
  options: { managedFileIds?: string[]; force?: boolean } = {}
): Promise<{ queued: number; targets: number }> => {
  const response = await authenticatedFetch(`${kbBase(customerID, kbId)}/generate`, {
    method: "POST",
    body: JSON.stringify(options),
  });
  if (!response.ok) {
    throw new Error(`generateDocumentCards failed: ${response.status}`);
  }
  return response.json();
};

export const patchDocumentCard = async (
  customerID: string,
  kbId: string,
  managedFileId: string,
  patch: Partial<Pick<DocumentCard, "title" | "synopsis" | "topics" | "entities">>
): Promise<DocumentCard> => {
  const response = await authenticatedFetch(
    `${kbBase(customerID, kbId)}/${managedFileId}`,
    { method: "PATCH", body: JSON.stringify(patch) }
  );
  if (!response.ok) {
    throw new Error(`patchDocumentCard failed: ${response.status}`);
  }
  return response.json();
};

export const deleteDocumentCard = async (
  customerID: string,
  kbId: string,
  managedFileId: string
): Promise<void> => {
  const response = await authenticatedFetch(
    `${kbBase(customerID, kbId)}/${managedFileId}`,
    { method: "DELETE" }
  );
  if (!response.ok && response.status !== 404) {
    throw new Error(`deleteDocumentCard failed: ${response.status}`);
  }
};
